import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { FindManyResult } from "@/core/common/resource-base/find-many-result.dto";
import { ProjectDto } from "@/core/features/projects/project.dto";
import { HttpRequest } from "@/core/common/http/http-request";
import { IRequest } from "@/interfaces/IRequest";
import { CommentProjectResourceDto } from "@/core/features/projects/comment-project-resource.dto";
import { CreateProjectHasVehicleDto } from "@/core/features/projects-have-vehicles/create-project-has-vehicle.dto";
import { ProjectHasVehicleDto } from "@/core/features/projects-have-vehicles/project-has-vehicle.dto";
import { UpdateProjectHasVehicleDto } from "@/core/features/projects-have-vehicles/update-project-has-vehicle.dto";
import { EditResourceDocStatusDto } from "@/core/features/projects-have-users/edit-resource-doc-status.dto";
import { plainToInstance } from "class-transformer";
import { DocumentationResourceStatusEnum } from "@altertec_gparn/lib";
import { OptionType } from "@/types/OptionType";
import { getOptionLoader } from "@/core/common/presenters/optionLoader";
import { CondOperator } from "@nestjsx/crud-request";
import { cloneDeep, merge } from "lodash";

export class ProjectsHaveVehiclesResource extends ResourceBaseService<
  ProjectHasVehicleDto,
  CreateProjectHasVehicleDto,
  UpdateProjectHasVehicleDto
> {
  protected EntityDto = ProjectHasVehicleDto;
  protected CreateEntityDto = CreateProjectHasVehicleDto;
  protected UpdateEntityDto = UpdateProjectHasVehicleDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/projects-have-vehicles");
  }

  async optionLoader(
    projectId: string,
    inputValue: string,
    includeArchived?: boolean,
    customRequest?: IRequest,
    excluded?: string[]
  ): Promise<OptionType[]> {
    const requestWithExcluded: IRequest = Object.assign({}, customRequest);
    if (excluded && excluded.length) {
      if (!requestWithExcluded.customAndFilters) requestWithExcluded.customAndFilters = [];
      requestWithExcluded.customAndFilters.push({ field: "vehicleId", value: excluded, operator: CondOperator.NOT_IN });
    }

    return getOptionLoader(
      inputValue,
      ["vehicleId", "vehicle.code", "vehicle.model", "vehicle.brand"],
      [
        { field: "vehicle.code", order: "ASC" },
        { field: "vehicle.model", order: "ASC" },
        { field: "vehicle.brand", order: "ASC" },
      ],
      (request) => this.findAll(merge(request, requestWithExcluded)),
      ["vehicle.code", "vehicle.model", "vehicle.brand"],
      { projectId: projectId },
      includeArchived
    );
  }

  async getVehicles(
    projectId: string,
    request: IRequest = {},
    includeArchived?: boolean,
    defaultSort = false
  ): Promise<FindManyResult<ProjectHasVehicleDto>> {
    const req = cloneDeep(request);
    if (!req.join) req.join = [];
    if (!req.join.includes("vehicle")) {
      req.join.push("vehicle");
    }
    if (req.search) {
      req.searchBy = ["vehicleId", "vehicle.code", "vehicle.model", "vehicle.brand"];
    }
    if (defaultSort) {
      req.sort = [
        { field: "vehicle.code", order: "ASC" },
        { field: "vehicle.model", order: "ASC" },
        { field: "vehicle.brand", order: "ASC" },
      ];
    }
    if (!req.filters) req.filters = {};
    Object.assign(req.filters, { projectId: projectId });

    return this.findAll(req, includeArchived);
  }

  changeResourceDocStatus(
    id: string,
    documentationResourceStatus: DocumentationResourceStatusEnum,
    projectId: string
  ): Promise<ProjectDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/change-documentation-resource-status",
        method: "PATCH",
        auth: true,
        body: plainToInstance(EditResourceDocStatusDto, {
          documentationResourceStatus: documentationResourceStatus,
          projectId: projectId,
        }),
      })
    );
  }

  setComment(id: string, comment: CommentProjectResourceDto): Promise<ProjectDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/set-comment",
        method: "PATCH",
        auth: true,
        body: plainToInstance(CommentProjectResourceDto, comment),
      })
    );
  }

  deleteComment(id: string): Promise<ProjectDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/delete-comment",
        method: "PATCH",
        auth: true,
        body: {},
      })
    );
  }
}
